import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useArtists } from '../../hooks/useArtists';

const HomePage = () => {
  const { artists } = useArtists();
  
  const featuredArtists = artists
    .filter(a => a.type === 'illustrator' || a.type === 'designer')
    .slice(0, 4);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/bcc45da53ce4fb5fb70f39064177ce64.jpg)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 text-center px-4 md:px-8 w-full">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6 tracking-tight">
            Beyond Art, We are Pavane
          </h2>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/95 mb-8 md:mb-12 max-w-3xl mx-auto leading-relaxed">
            파반느는 창작자의 고유한 가치를 발견하고, 클라이언트와 완벽한 시너지를 이끌어내는 크리에이티브 에이전시입니다.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
            <Link
              to="/illustrators"
              className="px-6 md:px-8 py-3 md:py-4 bg-white text-black font-semibold rounded-full hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              아티스트 보기
            </Link>
            <Link
              to="/contact"
              className="px-6 md:px-8 py-3 md:py-4 bg-transparent border-2 border-white text-white font-semibold rounded-full hover:bg-white hover:text-black transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              문의하기
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Artists Grid */}
      <section className="py-12 md:py-24 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-8 md:mb-16 text-center">주요 아티스트</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8 md:mb-16">
            {featuredArtists.map((artist) => (
              <Link key={artist.id} to={`/artist/${artist.slug}`} className="group cursor-pointer">
                <div className="relative overflow-hidden rounded-lg mb-4 aspect-[4/5]">
                  <img
                    src={artist.image}
                    alt={artist.name}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <h4 className="text-lg md:text-xl font-bold mb-1">{artist.name}</h4>
                <p className="text-sm text-gray-600">{artist.specialty}</p>
              </Link>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/illustrators"
              className="inline-flex items-center gap-2 text-sm font-semibold hover:gap-4 transition-all cursor-pointer"
            >
              모든 아티스트 보기
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Portfolio Section */}
      <section className="py-12 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-8 md:mb-16">
            <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">주요 포트폴리오</h3>
            <p className="text-gray-600 text-sm md:text-base max-w-2xl mx-auto">
              다양한 분야의 아티스트들이 만들어낸 뛰어난 작품들을 만나보세요
            </p>
          </div>
          
          <div className="columns-1 sm:columns-2 md:columns-3 gap-3 md:gap-5 space-y-3 md:space-y-5">
            {[
              { img: 'https://readdy.ai/api/search-image?query=Vibrant%20editorial%20illustration%20of%20a%20mysterious%20garden%20scene%20with%20bold%20warm%20colors%2C%20lush%20botanical%20elements%20and%20a%20solitary%20figure%2C%20modern%20graphic%20style%20with%20rich%20textures%20and%20layered%20compositions%2C%20contemporary%20art%20print%20quality%2C%20expressive%20and%20narrative%20visual%20storytelling&width=600&height=750&seq=sc-port1&orientation=portrait', title: 'The Silent Garden', artist: 'Sarah Chen', slug: 'sarah-chen', category: 'Editorial' },
              { img: 'https://readdy.ai/api/search-image?query=Clean%20modern%20brand%20identity%20design%20with%20minimalist%20logo%20on%20warm%20neutral%20background%2C%20elegant%20typography%20and%20color%20palette%20presentation%2C%20professional%20branding%20materials%2C%20high%20quality%20graphic%20design&width=600&height=750&seq=at-port1&orientation=portrait', title: 'Lumina Rebrand', artist: 'Alex Thompson', slug: 'alex-thompson', category: 'Brand Identity' },
              { img: 'https://readdy.ai/api/search-image?query=Colorful%20animated%20character%20designs%20of%20cute%20space%20creatures%20with%20playful%20expressions%2C%20bright%20warm%20colors%20and%20cartoon%20style%2C%20fun%20and%20engaging%20personality%2C%20professional%20animation%20art%2C%20clean%20white%20background&width=600&height=750&seq=mr-port1&orientation=portrait', title: 'Cosmic Companions', artist: 'Marcus Rodriguez', slug: 'marcus-rodriguez', category: 'Animation' },
              { img: 'https://readdy.ai/api/search-image?query=Elegant%20fashion%20illustration%20of%20a%20woman%20in%20Parisian%20haute%20couture%20with%20graceful%20lines%20and%20warm%20coral%20tones%2C%20sophisticated%20style%20with%20flowing%20fabric%20details%2C%20modern%20fashion%20art%2C%20professional%20quality%20print&width=600&height=750&seq=yt-port1&orientation=portrait', title: 'Parisian Elegance', artist: 'Yuki Tanaka', slug: 'yuki-tanaka', category: 'Fashion' },
              { img: 'https://readdy.ai/api/search-image?query=Epic%20fantasy%20concept%20art%20of%20ancient%20ruins%20with%20dramatic%20warm%20golden%20lighting%2C%20mystical%20architecture%20and%20atmospheric%20fog%2C%20professional%20digital%20painting%2C%20cinematic%20game%20art%20quality&width=600&height=750&seq=dp-port1&orientation=portrait', title: 'Realm of Ancients', artist: 'David Park', slug: 'david-park', category: 'Game Art' },
              { img: 'https://readdy.ai/api/search-image?query=Beautiful%20wellness%20mobile%20app%20UI%20design%20with%20warm%20soft%20colors%20and%20clean%20interface%2C%20meditation%20and%20health%20tracking%20screens%2C%20modern%20UX%20design%2C%20professional%20quality%20presentation%20on%20warm%20background&width=600&height=750&seq=np-port1&orientation=portrait', title: 'Wellness App', artist: 'Nina Patel', slug: 'nina-patel', category: 'Mobile App' },
              { img: 'https://readdy.ai/api/search-image?query=Bold%20summer%20advertising%20illustration%20with%20vibrant%20warm%20tropical%20colors%20and%20dynamic%20composition%2C%20eye-catching%20graphics%20with%20beach%20and%20lifestyle%20elements%2C%20modern%20commercial%20art%20style%2C%20professional%20quality&width=600&height=750&seq=sm-port1&orientation=portrait', title: 'Summer Vibes', artist: 'Sofia Martinez', slug: 'sofia-martinez', category: 'Campaign' },
              { img: 'https://readdy.ai/api/search-image?query=Elegant%20artisan%20tea%20packaging%20design%20with%20warm%20earthy%20tones%20and%20Japanese-inspired%20minimalist%20graphics%2C%20beautiful%20box%20and%20tin%20designs%2C%20professional%20product%20photography%2C%20high%20quality%20presentation&width=600&height=750&seq=jl-port1&orientation=portrait', title: 'Artisan Tea Collection', artist: 'James Lee', slug: 'james-lee', category: 'Packaging' },
              { img: 'https://readdy.ai/api/search-image?query=Charming%20children%20book%20illustration%20of%20magical%20forest%20with%20cute%20woodland%20animals%20and%20warm%20soft%20colors%2C%20whimsical%20trees%20and%20mushrooms%2C%20gentle%20textures%20and%20playful%20details%2C%20professional%20quality%20art%20print&width=600&height=750&seq=ew-port1&orientation=portrait', title: 'The Enchanted Forest', artist: 'Emma Williams', slug: 'emma-williams', category: 'Book' },
            ].map((work, index) => (
              <Link
                key={index}
                to={`/artist/${work.slug}`}
                className="group block break-inside-avoid cursor-pointer"
              >
                <div className="relative overflow-hidden rounded-lg bg-white shadow-sm hover:shadow-lg transition-shadow duration-300">
                  <div className="relative overflow-hidden">
                    <img
                      src={work.img}
                      alt={work.title}
                      className="w-full h-auto object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                      <span className="text-white text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-2 whitespace-nowrap">
                        포트폴리오 보기
                        <i className="ri-arrow-right-up-line"></i>
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <span className="text-xs font-medium text-rose-500 mb-1 block">{work.category}</span>
                    <h4 className="text-sm font-bold text-gray-900 mb-1">{work.title}</h4>
                    <p className="text-xs text-gray-500">{work.artist}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-8 md:mt-14">
            <Link
              to="/illustrators"
              className="inline-flex items-center gap-2 text-sm font-semibold hover:gap-4 transition-all cursor-pointer"
            >
              더 많은 작품 보기
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-32 px-4 md:px-8 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6">
            함께 멋진 작품을 만들어요
          </h3>
          <p className="text-lg md:text-xl text-white/95 mb-8 md:mb-12 leading-relaxed">
            재능 있는 아티스트들과 협력하여 당신의 크리에이티브 비전을 실현하세요
          </p>
          <Link
            to="/contact"
            className="inline-block px-8 md:px-10 py-4 md:py-5 bg-white text-black font-semibold rounded-full hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap text-sm"
          >
            프로젝트 시작하기
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
